import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPKR } from "../lib/api";
import { Button } from "../components/ui/button";
import { AlertTriangle, BellRing, PhoneCall, ChevronDown, ChevronRight, Phone, MapPin, ImageIcon, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import SendWhatsAppButton from "../components/SendWhatsAppButton";
import { RiskBadge, FlagPill } from "../components/CustomerBadges";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function Recovery() {
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [agents, setAgents] = useState([]);
  const load = () => api.get("/recovery/priority").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/agents").then((r) => setAgents(r.data || [])).catch(() => {}); }, []);

  const sendReminder = async (cid) => {
    await api.post(`/recovery/reminder/${cid}`);
    toast.success("Reminder sent (simulated)");
  };
  const logCall = async (cid, status) => {
    await api.post(`/followups`, { customer_id: cid, call_status: status, note: `Call: ${status}` });
    toast.success("Call logged");
  };

  const agentMap = Object.fromEntries(agents.map((a) => [a.id, a]));

  return (
    <div className="space-y-6" data-testid="recovery-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Recovery</h1>
        <p className="text-slate-500 mt-1">Top overdue customers — act on these today. Click row for full guarantor + photo info.</p>
      </div>

      {rows.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center">
          <div className="font-display text-xl font-bold text-emerald-700">All clear!</div>
          <div className="text-sm text-slate-500 mt-1">No overdue loans at the moment.</div>
        </div>
      )}

      <div className="space-y-3">
        {rows.map((l, i) => {
          const isOpen = !!expanded[l.id];
          const agent = l.assigned_agent_id ? agentMap[l.assigned_agent_id] : null;
          return (
            <div key={l.id} className="bg-white border-l-4 border-l-red-500 border border-slate-200 rounded-md overflow-hidden" data-testid={`recovery-row-${l.id}`}>
              <div className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                <button type="button" onClick={() => setExpanded({ ...expanded, [l.id]: !isOpen })} className="flex items-center gap-3 flex-1 text-left" data-testid={`recovery-toggle-${l.id}`}>
                  {l.customer_photo ? (
                    <img src={`${API_BASE}${l.customer_photo}`} alt={l.customer_name} className="w-12 h-12 rounded-full object-cover border border-slate-200" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-red-50 border border-red-200 grid place-items-center">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-lg flex items-center gap-1.5 flex-wrap">
                      <Link to={`/customers/${l.customer_id}`} className="hover:text-emerald-700" onClick={(e) => e.stopPropagation()}>{l.customer_name}</Link>
                      <span className="text-xs text-slate-400">#{i + 1}</span>
                      {l.risk_level && <RiskBadge level={l.risk_level} score={l.risk_score} />}
                      {l.blacklisted && <FlagPill kind="blacklisted" />}
                    </div>
                    <div className="text-xs text-slate-500 tabular flex items-center gap-2 mt-0.5">
                      <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{l.customer_mobile}</span>
                      {l.alternate_mobile && <span className="text-slate-400">/ {l.alternate_mobile}</span>}
                      <span>· {l.product_name}</span>
                      {l.recovery_zone && <span className="text-emerald-700 font-medium">· {l.recovery_zone}</span>}
                    </div>
                  </div>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 ml-2" /> : <ChevronRight className="w-4 h-4 text-slate-400 ml-2" />}
                </button>

                <div className="grid grid-cols-3 gap-4 text-sm md:text-right">
                  <div>
                    <div className="text-xs text-slate-500">Days late</div>
                    <div className="font-display font-bold text-red-600 tabular">{l.days_late}d</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Due amount</div>
                    <div className="font-display font-bold tabular">{fmtPKR(l.next_due?.amount || l.installment_value)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Remaining</div>
                    <div className="font-display font-bold tabular">{fmtPKR(l.remaining_amount)}</div>
                  </div>
                </div>

                <div className="flex gap-1.5 flex-wrap justify-end">
                  <SendWhatsAppButton
                    customer={{ name: l.customer_name, mobile: l.customer_mobile }}
                    loan={l}
                    variant="ghost"
                    defaultBucket="overdue"
                    testid={`recovery-wa-${l.id}`}
                  />
                  <Button size="sm" variant="outline" onClick={() => sendReminder(l.customer_id)} data-testid={`recovery-remind-${l.id}`}>
                    <BellRing className="w-4 h-4 mr-1 text-amber-600" /> Remind
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => logCall(l.customer_id, "called")} data-testid={`recovery-call-${l.id}`}>
                    <PhoneCall className="w-4 h-4 mr-1 text-emerald-600" /> Call
                  </Button>
                  <Link to={`/customers/${l.customer_id}`}>
                    <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`recovery-open-${l.id}`}>Open</Button>
                  </Link>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-slate-100 bg-slate-50 p-5 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid={`recovery-detail-${l.id}`}>
                  <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Customer Contact</div>
                    <div className="text-sm text-slate-800 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" />{l.customer_mobile}</div>
                    {l.alternate_mobile && <div className="text-sm text-slate-600 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" />{l.alternate_mobile} (alt)</div>}
                    {l.customer_address && <div className="text-sm text-slate-600 flex items-start gap-1.5 mt-1"><MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />{l.customer_address}</div>}
                    {agent && <div className="text-xs text-emerald-700 font-semibold mt-2">Agent: {agent.name} · {agent.recovery_zone}</div>}
                  </div>

                  {[1, 2].map((idx) => {
                    const g = idx === 1 ? l.guarantor1 : l.guarantor2;
                    if (!g || !g.name) return null;
                    return (
                      <div key={idx} data-testid={`recovery-g${idx}-${l.id}`}>
                        <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Guarantor {idx}</div>
                        <div className="flex items-center gap-2 mb-1">
                          {(g.documents || {}).photo ? (
                            <img src={`${API_BASE}${g.documents.photo}`} alt={g.name} className="w-10 h-10 rounded-full object-cover border" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-slate-200 grid place-items-center text-slate-400"><ImageIcon className="w-4 h-4" /></div>
                          )}
                          <div>
                            <div className="font-semibold text-sm">{g.name}</div>
                            <div className="text-xs text-slate-500">{g.relationship || ""}</div>
                          </div>
                        </div>
                        {g.mobile && <div className="text-sm text-slate-700 flex items-center gap-1.5 tabular"><Phone className="w-3.5 h-3.5 text-slate-400" /><a href={`tel:${g.mobile}`} className="hover:text-emerald-700">{g.mobile}</a></div>}
                        {g.cnic && <div className="text-xs text-slate-500 tabular">CNIC: {g.cnic}</div>}
                        {g.residence_address && <div className="text-xs text-slate-600 mt-1">{g.residence_address}</div>}
                      </div>
                    );
                  })}

                  {l.recovery_notes && (
                    <div className="md:col-span-3">
                      <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Recovery Notes</div>
                      <div className="text-sm text-slate-700 bg-white border border-slate-200 rounded p-2">{l.recovery_notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
